import React from 'react';
import { CoffeeSubmissionStatus, getCoffeeStatusColor, getCoffeeStatusMessage } from './CoffeeStatusTypes';

interface CoffeeStatusIndicatorProps {
    status: CoffeeSubmissionStatus;
    overlay?: boolean;
    inline?: boolean;
    message?: string;
    progress?: number;
    showProgress?: boolean;
    compact?: boolean;
}

const getStatusEmoji = (status: CoffeeSubmissionStatus): string => {
    switch (status) {
        case 'completed': return '✅';
        case 'creating':
        case 'uploading':
        case 'processing': return '☕';
        case 'validating':
        case 'retrying': return '🔄';
        case 'failed':
        case 'error': return '❌';
        default: return '⏳';
    }
};

const CoffeeStatusIndicator: React.FC<CoffeeStatusIndicatorProps> = ({
    status,
    overlay = false,
    inline = false,
    message,
    progress,
    showProgress = false,
    compact = false,
}) => {
    if (status === 'idle' && !overlay && !inline) {
        return null;
    }

    const statusColor = getCoffeeStatusColor(status);
    const statusMessage = getCoffeeStatusMessage(status);
    const emoji = getStatusEmoji(status);

    if (compact) {
        return (
            <div className="flex items-center gap-2">
                <span className="relative flex size-3">
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                        style={{ backgroundColor: statusColor }}
                    />
                    <span
                        className="relative inline-flex size-3 rounded-full"
                        style={{ backgroundColor: statusColor }}
                    />
                </span>
                <span className="text-sm font-medium" style={{ color: statusColor }}>
                    {statusMessage}
                </span>
            </div>
        );
    }

    if (overlay) {
        return (
            <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="bg-black/80 text-white px-6 py-4 rounded-xl shadow-xl text-center min-w-[200px]">
                    <div className="text-2xl mb-2">{emoji}</div>
                    <div className="text-base font-semibold" style={{ color: statusColor }}>
                        {statusMessage}
                    </div>
                    {message && (
                        <div className="text-sm text-white/80 mt-1">{message}</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className="w-full rounded-xl p-4 border-2 transition-colors"
            style={{ borderColor: statusColor, backgroundColor: `${statusColor}10` }}
        >
            <div className="flex items-center gap-3">
                <span className="relative flex size-4 shrink-0">
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                        style={{ backgroundColor: statusColor }}
                    />
                    <span
                        className="relative inline-flex size-4 rounded-full"
                        style={{ backgroundColor: statusColor }}
                    />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-base">{emoji}</span>
                        <span className="text-sm font-bold uppercase tracking-wide" style={{ color: statusColor }}>
                            {statusMessage}
                        </span>
                    </div>
                    {message && (
                        <p className="text-sm text-gray-600 mt-0.5 truncate">{message}</p>
                    )}
                </div>
            </div>

            {showProgress && typeof progress === 'number' && (
                <div className="mt-3">
                    <div className="w-full h-2 bg-black/5 rounded-full overflow-hidden">
                        <div
                            className="h-full rounded-full transition-all duration-500 ease-out"
                            style={{
                                width: `${Math.max(0, Math.min(100, progress))}%`,
                                backgroundColor: statusColor,
                            }}
                        />
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-right">
                        {Math.round(progress)}%
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoffeeStatusIndicator;